// Self-contained translations for the guest-facing InRoom page.
// Kept separate from the site-wide /src/i18n so the menu SKUs stay
// backend-owned while still being displayed in the guest's language.
//
// Language detection order:
//   1. `?lang=` query string
//   2. `/{lang}/inroom/...` path prefix (already handled by LanguageRouteSync)
//   3. `navigator.language` (browser locale)
//   4. English fallback

const SUPPORTED = ['en', 'me', 'sq', 'de', 'ru', 'pl', 'hu'];

// Menu item names keyed by SKU. Backend sends a stable SKU per line — we
// translate to the guest locale on the client.
const MENU_NAMES = {
  en: {
    water: 'Still water 0.5L', sparkling: 'Sparkling water 0.5L',
    coffee: 'Espresso', cappuccino: 'Cappuccino', tea: 'Herbal tea',
    juice: 'Fresh orange juice', beer: 'Local beer', wine_glass: 'House wine (glass)',
    club_sandwich: 'Club sandwich', cheese_plate: 'Cheese plate',
    fruit_plate: 'Fruit plate', salad_greek: 'Greek salad',
    fries: 'French fries', burger: 'Beef burger', pasta: 'Pasta of the day',
    bathrobe: 'Bathrobe (rental)', slippers: 'Slippers',
    iron: 'Iron & board', hairdryer: 'Hair dryer', adapter: 'Power adapter (EU/UK/US)',
  },
  me: {
    water: 'Negazirana voda 0.5L', sparkling: 'Gazirana voda 0.5L',
    coffee: 'Espresso', cappuccino: 'Cappuccino', tea: 'Biljni čaj',
    juice: 'Svježe cijeđeni sok od pomorandže', beer: 'Domaće pivo', wine_glass: 'Kućno vino (čaša)',
    club_sandwich: 'Klub sendvič', cheese_plate: 'Tanjir sireva',
    fruit_plate: 'Voćna tanjir', salad_greek: 'Grčka salata',
    fries: 'Pomfrit', burger: 'Burger', pasta: 'Testenina dana',
    bathrobe: 'Bade mantil (iznajmljivanje)', slippers: 'Papuče',
    iron: 'Pegla i daska', hairdryer: 'Fen', adapter: 'Adapter (EU/UK/US)',
  },
  sq: {
    water: 'Ujë i pastër 0.5L', sparkling: 'Ujë me gaz 0.5L',
    coffee: 'Espresso', cappuccino: 'Cappuccino', tea: 'Çaj bimor',
    juice: 'Lëng portokalli i freskët', beer: 'Birrë vendore', wine_glass: 'Verë shtëpie (gotë)',
    club_sandwich: 'Klub sandwich', cheese_plate: 'Pjatë djathi',
    fruit_plate: 'Pjatë frutash', salad_greek: 'Sallatë greke',
    fries: 'Patate të skuqura', burger: 'Burger viçi', pasta: 'Makarona e ditës',
    bathrobe: 'Rrobë banje (me qira)', slippers: 'Pantofla',
    iron: 'Hekur & tavolinë', hairdryer: 'Tharëse flokësh', adapter: 'Adaptor rryme (EU/UK/US)',
  },
  de: {
    water: 'Stilles Wasser 0,5L', sparkling: 'Mineralwasser 0,5L',
    coffee: 'Espresso', cappuccino: 'Cappuccino', tea: 'Kräutertee',
    juice: 'Frisch gepresster Orangensaft', beer: 'Lokales Bier', wine_glass: 'Hauswein (Glas)',
    club_sandwich: 'Club-Sandwich', cheese_plate: 'Käseplatte',
    fruit_plate: 'Obstteller', salad_greek: 'Griechischer Salat',
    fries: 'Pommes frites', burger: 'Rindfleisch-Burger', pasta: 'Nudelgericht des Tages',
    bathrobe: 'Bademantel (Leihgabe)', slippers: 'Hausschuhe',
    iron: 'Bügeleisen & Brett', hairdryer: 'Haartrockner', adapter: 'Steckeradapter (EU/UK/US)',
  },
  ru: {
    water: 'Вода без газа 0,5л', sparkling: 'Газированная вода 0,5л',
    coffee: 'Эспрессо', cappuccino: 'Капучино', tea: 'Травяной чай',
    juice: 'Свежевыжатый апельсиновый сок', beer: 'Местное пиво', wine_glass: 'Домашнее вино (бокал)',
    club_sandwich: 'Клубный сэндвич', cheese_plate: 'Сырная тарелка',
    fruit_plate: 'Фруктовая тарелка', salad_greek: 'Греческий салат',
    fries: 'Картофель фри', burger: 'Бургер', pasta: 'Паста дня',
    bathrobe: 'Халат (аренда)', slippers: 'Тапочки',
    iron: 'Утюг и доска', hairdryer: 'Фен', adapter: 'Переходник (EU/UK/US)',
  },
  pl: {
    water: 'Woda niegazowana 0,5L', sparkling: 'Woda gazowana 0,5L',
    coffee: 'Espresso', cappuccino: 'Cappuccino', tea: 'Herbata ziołowa',
    juice: 'Świeżo wyciskany sok pomarańczowy', beer: 'Lokalne piwo', wine_glass: 'Wino domu (kieliszek)',
    club_sandwich: 'Klub sandwich', cheese_plate: 'Deska serów',
    fruit_plate: 'Talerz owoców', salad_greek: 'Sałatka grecka',
    fries: 'Frytki', burger: 'Burger wołowy', pasta: 'Makaron dnia',
    bathrobe: 'Szlafrok (wypożyczenie)', slippers: 'Kapcie',
    iron: 'Żelazko i deska', hairdryer: 'Suszarka', adapter: 'Adapter (EU/UK/US)',
  },
  hu: {
    water: 'Szénsavmentes víz 0,5L', sparkling: 'Szénsavas víz 0,5L',
    coffee: 'Eszpresszó', cappuccino: 'Cappuccino', tea: 'Gyógynövény tea',
    juice: 'Frissen facsart narancslé', beer: 'Helyi sör', wine_glass: 'Házi bor (pohár)',
    club_sandwich: 'Klub szendvics', cheese_plate: 'Sajttál',
    fruit_plate: 'Gyümölcstál', salad_greek: 'Görög saláta',
    fries: 'Sültkrumpli', burger: 'Marhaburger', pasta: 'Napi tészta',
    bathrobe: 'Fürdőköpeny (kölcsön)', slippers: 'Papucs',
    iron: 'Vasaló és deszka', hairdryer: 'Hajszárító', adapter: 'Elektromos adapter (EU/UK/US)',
  },
};

const SECTION_NAMES = {
  en: { beverages: 'Beverages', food: 'Food', amenities: 'Amenities' },
  me: { beverages: 'Piće', food: 'Hrana', amenities: 'Dodatna oprema' },
  sq: { beverages: 'Pije', food: 'Ushqim', amenities: 'Pajisje' },
  de: { beverages: 'Getränke', food: 'Speisen', amenities: 'Ausstattung' },
  ru: { beverages: 'Напитки', food: 'Еда', amenities: 'Удобства' },
  pl: { beverages: 'Napoje', food: 'Jedzenie', amenities: 'Udogodnienia' },
  hu: { beverages: 'Italok', food: 'Ételek', amenities: 'Kényelmi eszközök' },
};

const REQUEST_LABELS = {
  en: {
    fresh_towels: 'Fresh towels', extra_towels: 'Extra towels',
    room_cleaning: 'Room cleaning', make_up_room: 'Make up the room',
    extra_pillow: 'Extra pillow', extra_blanket: 'Extra blanket',
    toiletries: 'Toiletries (soap, shampoo)', toilet_paper: 'Toilet paper',
    maintenance: 'Something broken (maintenance)', other: 'Something else',
  },
  me: {
    fresh_towels: 'Svježi peškiri', extra_towels: 'Dodatni peškiri',
    room_cleaning: 'Čišćenje sobe', make_up_room: 'Sređivanje sobe',
    extra_pillow: 'Dodatni jastuk', extra_blanket: 'Dodatno ćebe',
    toiletries: 'Kozmetika (sapun, šampon)', toilet_paper: 'Toalet papir',
    maintenance: 'Nešto je pokvareno', other: 'Nešto drugo',
  },
  sq: {
    fresh_towels: 'Peshqirë të freskët', extra_towels: 'Peshqirë ekstra',
    room_cleaning: 'Pastrimi i dhomës', make_up_room: 'Rregullo dhomën',
    extra_pillow: 'Jastëk shtesë', extra_blanket: 'Batanije shtesë',
    toiletries: 'Kozmetikë (sapun, shampon)', toilet_paper: 'Letër higjienike',
    maintenance: 'Diçka është prishur', other: 'Diçka tjetër',
  },
  de: {
    fresh_towels: 'Frische Handtücher', extra_towels: 'Extra Handtücher',
    room_cleaning: 'Zimmerreinigung', make_up_room: 'Zimmer aufbereiten',
    extra_pillow: 'Extra Kissen', extra_blanket: 'Extra Decke',
    toiletries: 'Kosmetika (Seife, Shampoo)', toilet_paper: 'Toilettenpapier',
    maintenance: 'Etwas ist defekt (Wartung)', other: 'Etwas anderes',
  },
  ru: {
    fresh_towels: 'Свежие полотенца', extra_towels: 'Дополнительные полотенца',
    room_cleaning: 'Уборка номера', make_up_room: 'Убрать номер',
    extra_pillow: 'Дополнительная подушка', extra_blanket: 'Дополнительное одеяло',
    toiletries: 'Косметика (мыло, шампунь)', toilet_paper: 'Туалетная бумага',
    maintenance: 'Что-то сломано', other: 'Что-то ещё',
  },
  pl: {
    fresh_towels: 'Świeże ręczniki', extra_towels: 'Dodatkowe ręczniki',
    room_cleaning: 'Sprzątanie pokoju', make_up_room: 'Sprzątnij pokój',
    extra_pillow: 'Dodatkowa poduszka', extra_blanket: 'Dodatkowy koc',
    toiletries: 'Kosmetyki (mydło, szampon)', toilet_paper: 'Papier toaletowy',
    maintenance: 'Coś się zepsuło', other: 'Coś innego',
  },
  hu: {
    fresh_towels: 'Friss törölközők', extra_towels: 'Extra törölközők',
    room_cleaning: 'Szobatakarítás', make_up_room: 'Rendezze a szobát',
    extra_pillow: 'Extra párna', extra_blanket: 'Extra takaró',
    toiletries: 'Kozmetika (szappan, sampon)', toilet_paper: 'Toalettpapír',
    maintenance: 'Valami eltört', other: 'Valami más',
  },
};

const SURVEY_LABELS = {
  en: { cleanliness: 'Cleanliness', comfort: 'Overall comfort', bed: 'Bed & pillows',
        staff: 'Staff service', food: 'Food & beverages', value: 'Value for money', location: 'Location' },
  me: { cleanliness: 'Čistoća', comfort: 'Ukupan komfor', bed: 'Krevet i jastuci',
        staff: 'Osoblje', food: 'Hrana i piće', value: 'Odnos cijene i kvaliteta', location: 'Lokacija' },
  sq: { cleanliness: 'Pastërtia', comfort: 'Komforti i përgjithshëm', bed: 'Shtrati dhe jastëkët',
        staff: 'Shërbimi i stafit', food: 'Ushqimi dhe pijet', value: 'Vlera për paratë', location: 'Vendndodhja' },
  de: { cleanliness: 'Sauberkeit', comfort: 'Gesamtkomfort', bed: 'Bett & Kissen',
        staff: 'Service', food: 'Speisen & Getränke', value: 'Preis-Leistung', location: 'Lage' },
  ru: { cleanliness: 'Чистота', comfort: 'Общий комфорт', bed: 'Кровать и подушки',
        staff: 'Обслуживание', food: 'Еда и напитки', value: 'Соотношение цены и качества', location: 'Расположение' },
  pl: { cleanliness: 'Czystość', comfort: 'Ogólny komfort', bed: 'Łóżko i poduszki',
        staff: 'Obsługa', food: 'Jedzenie i napoje', value: 'Stosunek jakości do ceny', location: 'Lokalizacja' },
  hu: { cleanliness: 'Tisztaság', comfort: 'Általános kényelem', bed: 'Ágy és párnák',
        staff: 'Kiszolgálás', food: 'Ételek és italok', value: 'Ár-érték arány', location: 'Elhelyezkedés' },
};

const UI = {
  en: {
    kicker: 'Hotel La Rosa Negra · In-room', greeting: 'How can we make your stay perfect?',
    tabOrder: 'Order', tabRequest: 'Request', tabSurvey: 'Rate stay',
    yourName: 'Your name (optional)', complimentary: 'Complimentary',
    orderNote: 'Anything else? (allergies, delivery time…)',
    placeOrder: 'Place order', item: 'item', items: 'items', add: 'Add',
    reqIntro: "Tap what you need and we'll take care of it.",
    reqNote: 'Anything specific? (2 towels, tomorrow at 10am…)',
    sendRequest: 'Send request',
    surveyIntro: 'Your feedback helps us serve you better. Rate what you can.',
    surveyComment: 'Tell us what stood out (optional)',
    submitReview: 'Submit review',
    orderSuccess: 'Order received!', orderSuccessBody: (n, price) => `Your order of ${n} item(s) for ${price} has been sent to reception. Estimated 15-25 min.`,
    reqSuccess: 'Request sent!', reqSuccessBody: (t) => `We'll take care of "${t}" as soon as possible.`,
    surveySuccessBody: "We've saved your feedback. If anything wasn't perfect, please tell reception directly so we can fix it before you leave.",
    surveyThanks: (overall) => `Thank you — ${overall}/5!`,
    sendAnother: 'Send another', notFound: 'Room not found',
    chargeToRoom: (name) => `Charge to ${name}'s room`,
    chargeToRoomHint: "One-tap billing — items land on your invoice at check-out.",
    chargedNotice: (name) => `Charged to ${name}'s room`,
  },
  me: {
    kicker: 'Hotel La Rosa Negra · U sobi', greeting: 'Kako da vaš boravak bude savršen?',
    tabOrder: 'Naruči', tabRequest: 'Zatraži', tabSurvey: 'Ocijeni boravak',
    yourName: 'Vaše ime (opcionalno)', complimentary: 'Besplatno',
    orderNote: 'Nešto još? (alergije, vrijeme dostave…)',
    placeOrder: 'Naruči', item: 'stavka', items: 'stavki', add: 'Dodaj',
    reqIntro: 'Dodirnite šta trebate — pobrinućemo se.',
    reqNote: 'Nešto konkretno? (2 peškira, sutra u 10h…)',
    sendRequest: 'Pošalji zahtjev',
    surveyIntro: 'Vaše mišljenje nam pomaže da budemo bolji.',
    surveyComment: 'Recite nam šta se istaklo (opcionalno)',
    submitReview: 'Pošalji recenziju',
    orderSuccess: 'Narudžba primljena!', orderSuccessBody: (n, price) => `Vaša narudžba od ${n} stavki za ${price} je poslata recepciji. Očekivano vrijeme 15-25 min.`,
    reqSuccess: 'Zahtjev poslat!', reqSuccessBody: (t) => `Pobrinućemo se za "${t}" što je prije moguće.`,
    surveySuccessBody: 'Sačuvali smo vaš odgovor. Ako nešto nije bilo savršeno, molimo javite recepciji da to ispravimo prije vašeg odlaska.',
    surveyThanks: (overall) => `Hvala vam — ${overall}/5!`,
    sendAnother: 'Pošalji još', notFound: 'Soba nije pronađena',
    chargeToRoom: (name) => `Naplati na sobu — ${name}`,
    chargeToRoomHint: 'Naplaćuje se automatski — stavke idu na račun pri odjavi.',
    chargedNotice: (name) => `Naplaćeno na sobu — ${name}`,
  },
  sq: {
    kicker: 'Hotel La Rosa Negra · Në dhomë', greeting: 'Si mund ta bëjmë të përsosur qëndrimin tuaj?',
    tabOrder: 'Porosit', tabRequest: 'Kërko', tabSurvey: 'Vlerëso',
    yourName: 'Emri juaj (opsional)', complimentary: 'Falas',
    orderNote: 'Diçka tjetër? (alergji, ora e dorëzimit…)',
    placeOrder: 'Porosit', item: 'artikull', items: 'artikuj', add: 'Shto',
    reqIntro: 'Prekni atë që keni nevojë — do të kujdesemi.',
    reqNote: 'Diçka specifike? (2 peshqirë, nesër në 10:00…)',
    sendRequest: 'Dërgo kërkesën',
    surveyIntro: 'Reagimi juaj na ndihmon të përmirësohemi.',
    surveyComment: 'Na tregoni çfarë ju bëri përshtypje (opsional)',
    submitReview: 'Dërgo vlerësimin',
    orderSuccess: 'Porosia u pranua!', orderSuccessBody: (n, price) => `Porosia juaj prej ${n} artikuj për ${price} u dërgua në recepsion. Koha e vlerësuar 15-25 min.`,
    reqSuccess: 'Kërkesa u dërgua!', reqSuccessBody: (t) => `Do të kujdesemi për "${t}" sa më shpejt të jetë e mundur.`,
    surveySuccessBody: 'Ruajtëm reagimin tuaj. Nëse diçka nuk ishte perfekt, ju lutemi tregoni recepsionit që ta rregullojmë para se të largoheni.',
    surveyThanks: (overall) => `Faleminderit — ${overall}/5!`,
    sendAnother: 'Dërgo një tjetër', notFound: 'Dhoma nuk u gjet',
    chargeToRoom: (name) => `Ngarko në dhomën e ${name}`,
    chargeToRoomHint: 'Faturim automatik — artikujt shkojnë në faturën tuaj.',
    chargedNotice: (name) => `U ngarkua në dhomën e ${name}`,
  },
  de: {
    kicker: 'Hotel La Rosa Negra · Zimmer-Service', greeting: 'Wie machen wir Ihren Aufenthalt perfekt?',
    tabOrder: 'Bestellen', tabRequest: 'Anfordern', tabSurvey: 'Bewerten',
    yourName: 'Ihr Name (optional)', complimentary: 'Kostenlos',
    orderNote: 'Sonst noch etwas? (Allergien, Lieferzeit…)',
    placeOrder: 'Bestellen', item: 'Artikel', items: 'Artikel', add: 'Hinzufügen',
    reqIntro: 'Tippen Sie an, was Sie brauchen — wir kümmern uns.',
    reqNote: 'Etwas Konkretes? (2 Handtücher, morgen um 10 Uhr…)',
    sendRequest: 'Anfrage senden',
    surveyIntro: 'Ihr Feedback hilft uns, besser zu werden.',
    surveyComment: 'Was ist Ihnen aufgefallen? (optional)',
    submitReview: 'Bewertung senden',
    orderSuccess: 'Bestellung erhalten!', orderSuccessBody: (n, price) => `Ihre Bestellung von ${n} Artikel(n) über ${price} wurde an die Rezeption gesendet. Geschätzt 15-25 Min.`,
    reqSuccess: 'Anfrage gesendet!', reqSuccessBody: (t) => `Wir kümmern uns so schnell wie möglich um "${t}".`,
    surveySuccessBody: 'Wir haben Ihr Feedback gespeichert. Falls etwas nicht perfekt war, sagen Sie es bitte direkt der Rezeption, damit wir es vor Ihrer Abreise beheben.',
    surveyThanks: (overall) => `Danke — ${overall}/5!`,
    sendAnother: 'Weitere senden', notFound: 'Zimmer nicht gefunden',
    chargeToRoom: (name) => `Auf ${name}s Zimmer buchen`,
    chargeToRoomHint: 'Automatische Abrechnung — Artikel landen auf Ihrer Rechnung.',
    chargedNotice: (name) => `Auf ${name}s Zimmer gebucht`,
  },
  ru: {
    kicker: 'Отель La Rosa Negra · В номере', greeting: 'Как сделать ваш отдых идеальным?',
    tabOrder: 'Заказать', tabRequest: 'Запрос', tabSurvey: 'Оценить',
    yourName: 'Ваше имя (по желанию)', complimentary: 'Бесплатно',
    orderNote: 'Что-нибудь ещё? (аллергии, время доставки…)',
    placeOrder: 'Оформить заказ', item: 'позиция', items: 'позиций', add: 'Добавить',
    reqIntro: 'Нажмите на нужное — мы позаботимся.',
    reqNote: 'Что-то конкретное? (2 полотенца, завтра в 10:00…)',
    sendRequest: 'Отправить запрос',
    surveyIntro: 'Ваш отзыв поможет нам стать лучше.',
    surveyComment: 'Расскажите, что впечатлило (по желанию)',
    submitReview: 'Отправить отзыв',
    orderSuccess: 'Заказ принят!', orderSuccessBody: (n, price) => `Ваш заказ из ${n} позиций на сумму ${price} отправлен на ресепшн. Время доставки 15-25 мин.`,
    reqSuccess: 'Запрос отправлен!', reqSuccessBody: (t) => `Мы позаботимся о «${t}» как можно скорее.`,
    surveySuccessBody: 'Мы сохранили ваш отзыв. Если что-то было не идеально, скажите об этом на ресепшн, чтобы мы исправили до вашего отъезда.',
    surveyThanks: (overall) => `Спасибо — ${overall}/5!`,
    sendAnother: 'Отправить ещё', notFound: 'Номер не найден',
    chargeToRoom: (name) => `Записать на счёт ${name}`,
    chargeToRoomHint: 'Автоматический счёт — позиции попадут в ваш инвойс.',
    chargedNotice: (name) => `Записано на счёт ${name}`,
  },
  pl: {
    kicker: 'Hotel La Rosa Negra · W pokoju', greeting: 'Jak sprawić, by Twój pobyt był idealny?',
    tabOrder: 'Zamów', tabRequest: 'Poproś', tabSurvey: 'Oceń pobyt',
    yourName: 'Twoje imię (opcjonalnie)', complimentary: 'Bezpłatne',
    orderNote: 'Coś jeszcze? (alergie, godzina dostawy…)',
    placeOrder: 'Złóż zamówienie', item: 'pozycja', items: 'pozycji', add: 'Dodaj',
    reqIntro: 'Wybierz czego potrzebujesz — zajmiemy się tym.',
    reqNote: 'Coś konkretnego? (2 ręczniki, jutro o 10:00…)',
    sendRequest: 'Wyślij prośbę',
    surveyIntro: 'Twoja opinia pomaga nam być lepszymi.',
    surveyComment: 'Powiedz nam, co się wyróżniło (opcjonalnie)',
    submitReview: 'Wyślij recenzję',
    orderSuccess: 'Zamówienie przyjęte!', orderSuccessBody: (n, price) => `Twoje zamówienie ${n} pozycji za ${price} zostało wysłane do recepcji. Ok. 15-25 min.`,
    reqSuccess: 'Prośba wysłana!', reqSuccessBody: (t) => `Zajmiemy się "${t}" tak szybko jak to możliwe.`,
    surveySuccessBody: 'Zachowaliśmy Twoją opinię. Jeśli coś nie było idealne, powiedz o tym w recepcji, abyśmy mogli to naprawić przed wyjazdem.',
    surveyThanks: (overall) => `Dziękujemy — ${overall}/5!`,
    sendAnother: 'Wyślij kolejny', notFound: 'Pokój nie znaleziony',
    chargeToRoom: (name) => `Doliczyć do pokoju ${name}`,
    chargeToRoomHint: 'Automatyczny rachunek — pozycje trafią na Twoją fakturę.',
    chargedNotice: (name) => `Doliczono do pokoju ${name}`,
  },
  hu: {
    kicker: 'Hotel La Rosa Negra · Szobában', greeting: 'Hogyan tehetjük tökéletessé az itt tartózkodását?',
    tabOrder: 'Rendelés', tabRequest: 'Kérés', tabSurvey: 'Értékelés',
    yourName: 'Az Ön neve (opcionális)', complimentary: 'Ingyenes',
    orderNote: 'Bármi más? (allergiák, kézbesítési idő…)',
    placeOrder: 'Rendelés leadása', item: 'tétel', items: 'tétel', add: 'Hozzáad',
    reqIntro: 'Érintse meg amire szüksége van — gondoskodunk róla.',
    reqNote: 'Valami konkrét? (2 törölköző, holnap 10-kor…)',
    sendRequest: 'Kérés küldése',
    surveyIntro: 'Visszajelzése segít, hogy jobbak legyünk.',
    surveyComment: 'Mondja el, mi tetszett (opcionális)',
    submitReview: 'Értékelés küldése',
    orderSuccess: 'Rendelés fogadva!', orderSuccessBody: (n, price) => `${n} tételes rendelése ${price} értékben elküldve a recepcióra. Kb. 15-25 perc.`,
    reqSuccess: 'Kérés elküldve!', reqSuccessBody: (t) => `Gondoskodunk erről: "${t}" a lehető leghamarabb.`,
    surveySuccessBody: 'Elmentettük visszajelzését. Ha valami nem volt tökéletes, kérjük szóljon a recepciónak közvetlenül, hogy még indulása előtt orvosolhassuk.',
    surveyThanks: (overall) => `Köszönjük — ${overall}/5!`,
    sendAnother: 'Másik küldése', notFound: 'Szoba nem található',
    chargeToRoom: (name) => `Terhelje ${name} szobájára`,
    chargeToRoomHint: 'Automatikus számlázás — a tételek a számlájára kerülnek.',
    chargedNotice: (name) => `Terhelve ${name} szobájára`,
  },
};

export const detectInRoomLang = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    // 1. Query string wins
    const qs = new URLSearchParams(window.location.search).get('lang');
    if (qs && SUPPORTED.includes(qs)) return qs;
    // 2. Path prefix /me/inroom/... etc.
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    if (seg && SUPPORTED.includes(seg)) return seg;
    // 3. localStorage — respects user's earlier language pick on the main site
    const saved = localStorage.getItem('hotel-language');
    if (saved && SUPPORTED.includes(saved)) return saved;
    // 4. Browser locale — map sr → me
    const nav = (navigator.language || 'en').toLowerCase().split('-')[0];
    const mapped = nav === 'sr' ? 'me' : nav;
    if (SUPPORTED.includes(mapped)) return mapped;
  } catch { /* fall through */ }
  return 'en';
};

export const t = (lang, key) => (UI[lang] || UI.en)[key] ?? UI.en[key];

// Guest-facing order tracking (QR page) — received → preparing → on its way.
const ORDER_STATUS = {
  en: { title: 'Your order', new: 'Received by the bar', prepared: 'Being prepared — on its way soon', delivered: 'Delivered — enjoy!', cancelled: 'Cancelled — please call reception', recent: 'Recent orders', eta: 'Usually 15–25 min' },
  me: { title: 'Vaša narudžba', new: 'Primljeno u baru', prepared: 'U pripremi — uskoro stiže', delivered: 'Dostavljeno — prijatno!', cancelled: 'Otkazano — pozovite recepciju', recent: 'Nedavne narudžbe', eta: 'Obično 15–25 min' },
  sq: { title: 'Porosia juaj', new: 'Pranuar nga bari', prepared: 'Në përgatitje — së shpejti', delivered: 'Dorëzuar — ju bëftë mirë!', cancelled: 'Anuluar — telefononi recepsionin', recent: 'Porositë e fundit', eta: 'Zakonisht 15–25 min' },
  de: { title: 'Ihre Bestellung', new: 'Von der Bar erhalten', prepared: 'Wird zubereitet — gleich unterwegs', delivered: 'Geliefert — guten Appetit!', cancelled: 'Storniert — bitte Rezeption anrufen', recent: 'Letzte Bestellungen', eta: 'Meist 15–25 Min.' },
  ru: { title: 'Ваш заказ', new: 'Принят баром', prepared: 'Готовится — скоро будет у вас', delivered: 'Доставлен — приятного аппетита!', cancelled: 'Отменён — позвоните на ресепшн', recent: 'Недавние заказы', eta: 'Обычно 15–25 мин' },
  pl: { title: 'Twoje zamówienie', new: 'Przyjęte przez bar', prepared: 'W przygotowaniu — zaraz w drodze', delivered: 'Dostarczone — smacznego!', cancelled: 'Anulowane — zadzwoń do recepcji', recent: 'Ostatnie zamówienia', eta: 'Zwykle 15–25 min' },
  hu: { title: 'Az Ön rendelése', new: 'A bár megkapta', prepared: 'Készül — hamarosan úton', delivered: 'Kiszállítva — jó étvágyat!', cancelled: 'Törölve — hívja a recepciót', recent: 'Legutóbbi rendelések', eta: 'Általában 15–25 perc' },
};
export const orderStatusText = (lang, key) => (ORDER_STATUS[lang] || ORDER_STATUS.en)[key] ?? ORDER_STATUS.en[key];

// Guest request tracking: received → seen & on the way → done.
const REQUEST_STATUS = {
  en: { title: 'Your request', new: 'Received — housekeeping has been alerted', accepted: (who) => `Seen by ${who || 'our housekeeper'} — on the way to your room`, done: 'Done — anything else, just ask', dismissed: 'Closed by reception — please call us if still needed', wait: 'Usually within 15 min' },
  me: { title: 'Vaš zahtjev', new: 'Primljeno — sobarice su obaviještene', accepted: (who) => `Vidjela ${who || 'naša sobarica'} — stiže u vašu sobu`, done: 'Riješeno — za sve ostalo samo pitajte', dismissed: 'Zatvorila recepcija — pozovite nas ako i dalje treba', wait: 'Obično u roku od 15 min' },
  sq: { title: 'Kërkesa juaj', new: 'U pranua — pastruesit u njoftuan', accepted: (who) => `U pa nga ${who || 'pastruesja jonë'} — po vjen në dhomën tuaj`, done: 'U krye — për çdo gjë tjetër, pyesni', dismissed: 'U mbyll nga recepsioni — na telefononi nëse ende nevojitet', wait: 'Zakonisht brenda 15 min' },
  de: { title: 'Ihre Anfrage', new: 'Eingegangen — das Housekeeping wurde alarmiert', accepted: (who) => `Gesehen von ${who || 'unserem Housekeeping'} — unterwegs zu Ihrem Zimmer`, done: 'Erledigt — fragen Sie jederzeit', dismissed: 'Von der Rezeption geschlossen — bitte anrufen, falls weiterhin nötig', wait: 'Meist innerhalb von 15 Min.' },
  ru: { title: 'Ваш запрос', new: 'Получен — горничные оповещены', accepted: (who) => `Увидел(а) ${who || 'наша горничная'} — уже идёт к вам`, done: 'Выполнено — обращайтесь в любое время', dismissed: 'Закрыт ресепшн — позвоните, если всё ещё нужно', wait: 'Обычно в течение 15 мин' },
  pl: { title: 'Twoja prośba', new: 'Otrzymano — housekeeping został powiadomiony', accepted: (who) => `Zobaczone przez ${who || 'naszą pokojową'} — już w drodze do pokoju`, done: 'Załatwione — w razie czego pytaj', dismissed: 'Zamknięte przez recepcję — zadzwoń, jeśli nadal potrzebne', wait: 'Zwykle do 15 min' },
  hu: { title: 'Az Ön kérése', new: 'Megérkezett — a takarítókat értesítettük', accepted: (who) => `Látta: ${who || 'takarítónk'} — már úton a szobájához`, done: 'Elintézve — bármi másban szívesen segítünk', dismissed: 'A recepció lezárta — hívjon, ha még szükséges', wait: 'Általában 15 percen belül' },
};
export const requestStatusText = (lang, key, who) => {
  const v = (REQUEST_STATUS[lang] || REQUEST_STATUS.en)[key] ?? REQUEST_STATUS.en[key];
  return typeof v === 'function' ? v(who) : v;
};

// Lounge-terrace ordering (QR per table / baldachin).
const TERRACE = {
  en: { kicker: 'Hotel La Rosa Negra · Lounge terrace', table: 'Table', baldachin: 'Baldachin', greeting: 'Order here — we bring it to you.', payTitle: 'How would you like to pay?', payBar: 'Pay at the bar (cash / card when served)', payRoom: 'Charge to my room', roomNumber: 'Room number', surname: 'Surname on the booking', roomMatched: (n) => `Charged to room ${n}.`, roomNotMatched: 'We could not match that room and surname — please pay when served.', successBody: (n, price, spot) => `Your order of ${n} item(s) for ${price} is on its way to ${spot}.`, notFound: 'Unknown table — please ask our staff' },
  me: { kicker: 'Hotel La Rosa Negra · Lounge terasa', table: 'Sto', baldachin: 'Baldahin', greeting: 'Naručite ovdje — donosimo vam.', payTitle: 'Kako želite platiti?', payBar: 'Plaćanje u baru (gotovina / kartica pri usluzi)', payRoom: 'Na račun sobe', roomNumber: 'Broj sobe', surname: 'Prezime sa rezervacije', roomMatched: (n) => `Zaduženo na sobu ${n}.`, roomNotMatched: 'Nismo pronašli tu sobu i prezime — molimo platite pri usluzi.', successBody: (n, price, spot) => `Vaša narudžba od ${n} stavki za ${price} stiže na ${spot}.`, notFound: 'Nepoznat sto — pitajte osoblje' },
  sq: { kicker: 'Hotel La Rosa Negra · Tarraca lounge', table: 'Tavolina', baldachin: 'Baldakin', greeting: 'Porositni këtu — ju a sjellim.', payTitle: 'Si dëshironi të paguani?', payBar: 'Paguaj në bar (para / kartë kur shërbehet)', payRoom: 'Faturoje në dhomën time', roomNumber: 'Numri i dhomës', surname: 'Mbiemri në rezervim', roomMatched: (n) => `U faturua në dhomën ${n}.`, roomNotMatched: 'Nuk e gjetëm atë dhomë dhe mbiemër — ju lutemi paguani kur shërbeheni.', successBody: (n, price, spot) => `Porosia juaj me ${n} artikuj për ${price} po vjen te ${spot}.`, notFound: 'Tavolinë e panjohur — pyesni stafin' },
  de: { kicker: 'Hotel La Rosa Negra · Lounge-Terrasse', table: 'Tisch', baldachin: 'Baldachin', greeting: 'Hier bestellen — wir bringen es Ihnen.', payTitle: 'Wie möchten Sie zahlen?', payBar: 'An der Bar zahlen (bar / Karte beim Servieren)', payRoom: 'Auf mein Zimmer buchen', roomNumber: 'Zimmernummer', surname: 'Nachname der Buchung', roomMatched: (n) => `Auf Zimmer ${n} gebucht.`, roomNotMatched: 'Zimmer und Nachname passen nicht zusammen — bitte beim Servieren zahlen.', successBody: (n, price, spot) => `Ihre Bestellung mit ${n} Artikel(n) für ${price} ist unterwegs zu ${spot}.`, notFound: 'Unbekannter Tisch — bitte Personal fragen' },
  ru: { kicker: 'Hotel La Rosa Negra · Лаунж-терраса', table: 'Стол', baldachin: 'Балдахин', greeting: 'Заказывайте здесь — мы принесём.', payTitle: 'Как вы хотите оплатить?', payBar: 'Оплата в баре (наличные / карта при подаче)', payRoom: 'Записать на номер', roomNumber: 'Номер комнаты', surname: 'Фамилия в бронировании', roomMatched: (n) => `Записано на номер ${n}.`, roomNotMatched: 'Номер и фамилия не совпали — пожалуйста, оплатите при подаче.', successBody: (n, price, spot) => `Ваш заказ из ${n} позиций на ${price} уже в пути к ${spot}.`, notFound: 'Неизвестный стол — обратитесь к персоналу' },
  pl: { kicker: 'Hotel La Rosa Negra · Taras lounge', table: 'Stolik', baldachin: 'Baldachim', greeting: 'Zamów tutaj — przyniesiemy.', payTitle: 'Jak chcesz zapłacić?', payBar: 'Płatność w barze (gotówka / karta przy podaniu)', payRoom: 'Dolicz do mojego pokoju', roomNumber: 'Numer pokoju', surname: 'Nazwisko z rezerwacji', roomMatched: (n) => `Doliczono do pokoju ${n}.`, roomNotMatched: 'Nie znaleźliśmy tego pokoju i nazwiska — prosimy zapłacić przy podaniu.', successBody: (n, price, spot) => `Twoje zamówienie (${n} poz.) za ${price} jest w drodze do ${spot}.`, notFound: 'Nieznany stolik — zapytaj obsługę' },
  hu: { kicker: 'Hotel La Rosa Negra · Lounge terasz', table: 'Asztal', baldachin: 'Baldachin', greeting: 'Rendeljen itt — mi kivisszük.', payTitle: 'Hogyan szeretne fizetni?', payBar: 'Fizetés a bárban (készpénz / kártya felszolgáláskor)', payRoom: 'A szobámra terhelve', roomNumber: 'Szobaszám', surname: 'Vezetéknév a foglaláson', roomMatched: (n) => `A(z) ${n}. szobára terhelve.`, roomNotMatched: 'Nem találtuk ezt a szobát és nevet — kérjük, fizessen felszolgáláskor.', successBody: (n, price, spot) => `${n} tételes rendelése (${price}) úton van ide: ${spot}.`, notFound: 'Ismeretlen asztal — kérdezze a személyzetet' },
};
export const terraceText = (lang, key) => (TERRACE[lang] || TERRACE.en)[key] ?? TERRACE.en[key];

const CALLS = {
  en: { waiter: 'Call a waiter', bill: 'The bill, please', sent: 'Our team is on the way to you.', already: 'We already got your call — coming!', done: 'Taken care of' },
  me: { waiter: 'Pozovite konobara', bill: 'Račun, molim', sent: 'Naš tim dolazi do vas.', already: 'Poziv je već primljen — stižemo!', done: 'Riješeno' },
  sq: { waiter: 'Thirrni kamarierin', bill: 'Faturën, ju lutem', sent: 'Ekipi ynë po vjen te ju.', already: 'E morëm thirrjen — po vijmë!', done: 'U krye' },
  de: { waiter: 'Kellner rufen', bill: 'Die Rechnung, bitte', sent: 'Unser Team ist auf dem Weg zu Ihnen.', already: 'Ihr Ruf ist angekommen — wir kommen!', done: 'Erledigt' },
  ru: { waiter: 'Позвать официанта', bill: 'Счёт, пожалуйста', sent: 'Наша команда уже идёт к вам.', already: 'Мы уже получили ваш вызов — идём!', done: 'Выполнено' },
  pl: { waiter: 'Zawołaj kelnera', bill: 'Poproszę rachunek', sent: 'Nasz zespół już do Ciebie idzie.', already: 'Mamy Twoje wezwanie — już idziemy!', done: 'Załatwione' },
  hu: { waiter: 'Pincér hívása', bill: 'A számlát kérem', sent: 'Csapatunk már úton van Önhöz.', already: 'Megkaptuk a hívást — jövünk!', done: 'Elintézve' },
};
export const callText = (lang, key) => (CALLS[lang] || CALLS.en)[key] ?? CALLS.en[key];
// If the backend already provided a localised name in the /rooms/{id}?lang=..
// response (via the new per-SKU translations), the caller passes that as
// `fallback` and we return it as-is. Otherwise fall through to the built-in
// dictionary, then to the raw SKU.
export const menuName = (lang, sku, fallback) => {
  const dict = (MENU_NAMES[lang] || MENU_NAMES.en);
  return dict[sku] || fallback || sku;
};
export const sectionName = (lang, section) =>
  (SECTION_NAMES[lang] || SECTION_NAMES.en)[section] || section;
export const requestLabel = (lang, key) =>
  (REQUEST_LABELS[lang] || REQUEST_LABELS.en)[key] || key;
export const surveyLabel = (lang, key) =>
  (SURVEY_LABELS[lang] || SURVEY_LABELS.en)[key] || key;

export const SUPPORTED_INROOM_LANGS = SUPPORTED;
